import React, {
  useState,
  useEffect,
  useCallback,
  Suspense,
  lazy,
} from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { CommandsContext } from "@/context/CommandsContext";

// Lazy-loaded so their heavy deps (tabs, select, date picker) stay code-split
// and don't bloat the initial app bundle — they only load when first opened.
const CreateLinkModal = lazy(() => import("@/components/app/CreateLinkModal"));
const ShortcutsHelpModal = lazy(
  () => import("@/components/app/ShortcutsHelpModal"),
);
const OutOfCreditsModal = lazy(
  () => import("@/components/app/OutOfCreditsModal"),
);

// ---------------------------------------------------------------------------
// Route title map — maps pathname prefixes to human-readable titles
// ---------------------------------------------------------------------------

const ROUTE_TITLES: Record<string, string> = {
  "/app/dashboard": "Dashboard",
  "/app/links": "Links",
  "/app/tags": "Tags",
  "/app/domains": "Domains",
  "/app/api-keys": "API Keys",
  "/app/billing": "Billing",
  "/app/profile": "Profile & Settings",
};

function resolveTitle(pathname: string): string | undefined {
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];

  const sorted = Object.keys(ROUTE_TITLES).sort((a, b) => b.length - a.length);
  for (const prefix of sorted) {
    if (pathname.startsWith(prefix)) return ROUTE_TITLES[prefix];
  }

  return undefined;
}

// ---------------------------------------------------------------------------
// Keyboard-shortcut → route map for the "g then …" sequence
// ---------------------------------------------------------------------------

const GO_TO: Record<string, string> = {
  d: "/app/dashboard",
  l: "/app/links",
  t: "/app/tags",
  m: "/app/domains",
  k: "/app/api-keys",
  b: "/app/billing",
  p: "/app/profile",
};

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return (
    el.isContentEditable ||
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.tagName === "SELECT"
  );
}

// ---------------------------------------------------------------------------
// AppLayout
// ---------------------------------------------------------------------------

const AppLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [outOfCredits, setOutOfCredits] = useState(false);

  const pageTitle = resolveTitle(location.pathname);

  const openCreateLink = useCallback(() => setCreateOpen(true), []);
  const openShortcutsHelp = useCallback(() => setHelpOpen(true), []);

  // ── Global keyboard shortcuts ─────────────────────────────────────────────
  useEffect(() => {
    let pendingG = false;
    let gTimer: ReturnType<typeof setTimeout> | undefined;

    function onKeyDown(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;

      // "?" toggles help even over other content
      if (e.key === "?") {
        e.preventDefault();
        setHelpOpen((v) => !v);
        return;
      }

      // Don't fire create/nav while a modal/dialog is open
      if (document.querySelector('[role="dialog"]')) return;

      const key = e.key.toLowerCase();

      // "g" then a letter → navigate
      if (pendingG) {
        pendingG = false;
        if (gTimer) clearTimeout(gTimer);
        const dest = GO_TO[key];
        if (dest) {
          e.preventDefault();
          navigate(dest);
        }
        return;
      }

      if (key === "g") {
        pendingG = true;
        if (gTimer) clearTimeout(gTimer);
        gTimer = setTimeout(() => {
          pendingG = false;
        }, 900);
        return;
      }

      if (key === "n") {
        e.preventDefault();
        setCreateOpen(true);
        return;
      }

      if (key === "s") {
        e.preventDefault();
        const el = document.getElementById(
          "quick-create-url",
        ) as HTMLInputElement | null;
        if (el) {
          el.focus();
          el.select();
        } else {
          navigate("/app/dashboard");
          // Focus once the dashboard's field has mounted
          setTimeout(() => {
            document.getElementById("quick-create-url")?.focus();
          }, 280);
        }
        return;
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (gTimer) clearTimeout(gTimer);
    };
  }, [navigate]);

  return (
    <CommandsContext.Provider value={{ openCreateLink, openShortcutsHelp }}>
      <div className="flex h-screen overflow-hidden bg-background">
        {/* ── Sidebar ─────────────────────────────────────────────────────── */}
        <Sidebar
          variant="user"
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {/* ── Right column: header + main content ──────────────────────────── */}
        <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
          <div className="shrink-0">
            <Header
              title={pageTitle}
              variant="user"
              onMenuClick={() => setSidebarOpen(true)}
            />
          </div>

          <main
            id="main-content"
            tabIndex={-1}
            className="flex-1 overflow-y-auto focus:outline-none"
            aria-label="Page content"
          >
            <div className="mx-auto w-full max-w-screen-xl px-4 py-6 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {/* ── Global modals (mounted only once first opened) ─────────────────── */}
      <Suspense fallback={null}>
        {createOpen && (
          <CreateLinkModal
            isOpen={createOpen}
            onClose={() => setCreateOpen(false)}
            onSuccess={() => {
              setCreateOpen(false);
              queryClient.invalidateQueries({ queryKey: ["links"] });
            }}
            onInsufficientBalance={() => {
              setCreateOpen(false);
              setOutOfCredits(true);
            }}
          />
        )}
        {helpOpen && (
          <ShortcutsHelpModal
            isOpen={helpOpen}
            onClose={() => setHelpOpen(false)}
          />
        )}
        {outOfCredits && (
          <OutOfCreditsModal
            isOpen={outOfCredits}
            onClose={() => setOutOfCredits(false)}
          />
        )}
      </Suspense>
    </CommandsContext.Provider>
  );
};

export default AppLayout;